import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, Typography, Box, CircularProgress, Tooltip, IconButton, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';

const DataLakeStatusTile = ({ sx }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);
  
  const API_URL = `${process.env.REACT_APP_API_URL}/system-info/datalake`;
  const API_KEY = process.env.REACT_APP_API_KEY;

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL, {
        headers: {
          'x-api-key': API_KEY
        }
      });
      setStatus(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching Data Lake status:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto refresh every 5 minutes
    const interval = setInterval(fetchStatus, 300000);
    return () => clearInterval(interval);
  }, []);

  const handleForceUpdate = async () => {
    try {
      setTriggering(true);
      await axios.post(`${API_URL}/trigger`, {}, {
        headers: {
          'x-api-key': API_KEY
        }
      });
      // Wait a moment before refreshing status
      setTimeout(() => {
        fetchStatus();
        setTriggering(false);
      }, 2000);
    } catch (err) {
      console.error('Error triggering Data Lake update:', err);
      setError(err);
      setTriggering(false);
    }
  };

  if (loading && !status) {
    return (
      <Card sx={{ ...sx, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ ...sx, p: 2 }}>
        <Typography color="error">Error loading Data Lake status</Typography>
      </Card>
    );
  }

  const etlStatus = status?.etlJob?.status || 'UNKNOWN';
  const crawlerState = status?.crawler?.state || 'UNKNOWN';
  
  const isRunning = etlStatus === 'RUNNING' || crawlerState === 'RUNNING';

  return (
    <Card sx={{ ...sx, display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Titicaca Data Lake</Typography>
          <Tooltip title="Status of the AWS Glue ETL and Schema Crawler pipeline.">
            <InfoIcon color="action" />
          </Tooltip>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1" color="text.secondary">ETL Status</Typography>
          <Typography variant="body1" sx={{ fontWeight: 'medium', color: etlStatus === 'SUCCEEDED' ? 'success.main' : etlStatus === 'RUNNING' ? 'info.main' : 'error.main' }}>
            {etlStatus}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1" color="text.secondary">Crawler Status</Typography>
          <Typography variant="body1" sx={{ fontWeight: 'medium', color: crawlerState === 'READY' ? 'success.main' : 'info.main' }}>
            {crawlerState}
          </Typography>
        </Box>
        
        {status?.etlJob?.completedOn && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Last completion: {new Date(status.etlJob.completedOn).toLocaleString()}
          </Typography>
        )}

        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            fullWidth 
            onClick={handleForceUpdate} 
            disabled={isRunning || triggering}
            startIcon={(isRunning || triggering) ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          >
            {isRunning ? 'Pipeline Running...' : 'Force Data Lake Update'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DataLakeStatusTile;
